/** Mirrors `DINING_ORDERS_PAYMENT_METHODS` in br-clubs-backend-reformed */
export const DINING_ORDER_PAYMENT_METHODS = [
  "wallet",
  "visa",
  "apple_pay",
] as const;

export type DiningOrderPaymentMethod =
  (typeof DINING_ORDER_PAYMENT_METHODS)[number];

/** Mirrors `DINING_ORDERS_SOURCES` */
export const DINING_ORDER_SOURCES = ["app", "admin_panel"] as const;
export type DiningOrderSource = (typeof DINING_ORDER_SOURCES)[number];

/** Mirrors `DINING_ORDERS_DELIVERY_METHODS` */
export const DINING_ORDER_DELIVERY_METHODS = ["delivery", "pickup"] as const;
export type DiningOrderDeliveryMethod =
  (typeof DINING_ORDER_DELIVERY_METHODS)[number];

/** Mirrors `DINING_ORDERS_STAGES` */
export const DINING_ORDER_STAGES = [
  "pending",
  "in_preparation",
  "ready_for_pickup",
  "delivery_accepted",
  "delivery_picked_up",
  "delivered",
  "cancelled",
] as const;
export type DiningOrderStage = (typeof DINING_ORDER_STAGES)[number];

/** `DiningOrderDTO` — br-clubs-backend-reformed */
export interface DiningOrderDTO {
  id: string;
  restaurantId: string;
  memberId: string;
  source: string;
  stage: string;
  deliveryMethod: string;
  waiterId?: string | null;
  zoneId?: string | null;
  subzoneId?: string | null;
  locationDetails?: string | null;
  specialRequest?: string | null;
  subtotal: number;
  tax: number;
  discount: number;
  deliveryFees: number;
  tips: number;
  total: number;
  paymentMethod: DiningOrderPaymentMethod | (string & {});
  paymentStatus: string;
  transactionId: string;
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

/** List member — `Pick<MemberDTO, 'id' | 'membershipId' | 'fullName' | 'firstName' | 'lastName' | 'phone'>` */
export interface DiningOrderListMember {
  id: string;
  membershipId?: string;
  fullName: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
}

/** Details member — repo attributes + `profileImage` association */
export interface DiningOrderDetailsMember extends DiningOrderListMember {
  translations?: Record<string, unknown>;
  profileImage?: { url: string } | null;
}

/** List transaction — `Pick<TransactionDTO, 'id' | 'walletTxnId' | 'visaTxnId'>` */
export interface DiningOrderTransaction {
  id: string;
  walletTxnId?: string | null;
  visaTxnId?: string | null;
}

/**
 * GET /api/v2/dining/restaurants/:restaurantId/orders
 * `RestaurantOrdersWithPaginationDTO.diningOrders[]`
 */
export interface DiningOrderListItem extends DiningOrderDTO {
  member: DiningOrderListMember;
  transaction: DiningOrderTransaction;
}

export interface RestaurantOrdersListResponse {
  diningOrders: DiningOrderListItem[];
  pagination: {
    totalCount: number;
  };
}

/**
 * Details restaurant — `DiningOrderWithDetailsDTO.restaurant`
 * Repo also returns `id`; DTO Pick omits it but it is present at runtime.
 */
export interface DiningOrderRestaurant {
  id: string;
  name: string;
  availability: string;
  normalDeliveryMins: number;
  busyDeliveryMins: number;
  translations?: Record<string, unknown>;
  iconImage: { url: string };
}

/**
 * `OrderedProductsWithDetailsDTO` + repo-selected fields.
 * Repo fetches product/variantOption without `price` (DTO class lists price; runtime does not).
 */
export interface DiningOrderedProduct {
  id: string;
  quantity: number;
  totalPrice: number;
  specialRequest?: string | null;
  productId?: string;
  categoryId?: string;
  category: {
    name: string;
    translations?: Record<string, unknown>;
  };
  product: {
    name: string;
    translations?: Record<string, unknown>;
  };
  orderedOptions: {
    price: number;
    variantOption: {
      name: string;
      translations?: Record<string, unknown>;
    };
  }[];
}

/** `DiningOrderWithDetailsDTO.waiter` */
export interface DiningOrderWaiter {
  name: string;
  employeeId?: string | null;
  phone: string;
  translations?: Record<string, unknown>;
  image?: { url: string } | null;
}

export interface DiningOrderZone {
  name: string;
  translations?: Record<string, unknown>;
}

/**
 * GET /api/v2/dining/restaurants/:restaurantId/orders/:orderId
 * `DiningOrderWithDetailsDTO` — no `transaction` on this response.
 */
export interface DiningOrderDetails extends DiningOrderDTO {
  member: DiningOrderDetailsMember;
  restaurant: DiningOrderRestaurant;
  orderedProducts: DiningOrderedProduct[];
  waiter?: DiningOrderWaiter | null;
  zone?: DiningOrderZone | null;
  subzone?: DiningOrderZone | null;
}

export type DiningOrder = DiningOrderListItem;
export type Order = DiningOrderDetails;

export const initialOrderValues: Order = {
  id: "",
  restaurantId: "",
  memberId: "",
  tax: 0,
  tips: 0,
  stage: "pending",
  total: 0,
  paymentStatus: "",
  member: {
    id: "",
    fullName: "",
    lastName: "",
    firstName: "",
    phone: "",
    membershipId: "",
    profileImage: null,
  },
  source: "app",
  zoneId: null,
  discount: 0,
  subtotal: 0,
  waiterId: null,
  createdAt: "",
  updatedAt: null,
  deletedAt: null,
  subzoneId: null,
  deliveryFees: 0,
  paymentMethod: "wallet",
  transactionId: "",
  deliveryMethod: "pickup",
  specialRequest: null,
  locationDetails: null,
  restaurant: {
    id: "",
    name: "",
    availability: "open",
    normalDeliveryMins: 0,
    busyDeliveryMins: 0,
    iconImage: { url: "" },
  },
  waiter: null,
  zone: null,
  subzone: null,
  orderedProducts: [],
};
